/**
 * GovRAMP + FedRAMP Class A screen model — V103-3-6 / V103-6-3 (design pass
 * `v103-programs-ui` round 4). Pure: no React. The program page, Today and
 * the Class A page all render from these functions, so they show the same
 * status line and the same single next action.
 *
 * Product rules encoded here (design summary):
 *   - Status before metric; the nearest hard date wins the one primary.
 *   - "No evidence" is not "Not met": no evidence → Collect, not met → Fix.
 *   - No computed GovRAMP lapse dates: a lapse is the recorded notice only.
 *   - Tier change never claims a tier GovRAMP hasn't granted ("Core held ·
 *     pursuing Ready"), and it waits while ConMon is late.
 *   - Class A hero counts come from the same totals as the legend
 *     (acceptance #1); the Ready booking is one record (acceptance #4).
 */
import type { ClassAClock, ClassAMaterialsItem, FrameworkReadiness, GovrampOverview } from './govramp-api';

export type GovrampCycle = GovrampOverview['cycles'][number];
export type Tone = 'ok' | 'warn' | 'bad';

const DAY_MS = 86_400_000;
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "Oct 15" (this year) or "Jan 9, 2027". */
export function fmtShort(d: string, todayDay: string): string {
  const [y, m, dd] = d.slice(0, 10).split('-').map(Number) as [number, number, number];
  if (!y || !m || !dd) return d;
  return `${SHORT[m - 1]} ${dd}${String(y) === todayDay.slice(0, 4) ? '' : `, ${y}`}`;
}
export function fmtLong(d: string): string {
  const [y, m, dd] = d.slice(0, 10).split('-').map(Number) as [number, number, number];
  return y && m && dd ? `${SHORT[m - 1]} ${dd}, ${y}` : d;
}
export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to.slice(0, 10)}T00:00:00Z`) - Date.parse(`${from.slice(0, 10)}T00:00:00Z`)) / DAY_MS);
}
export const daysText = (n: number) => (n === 0 ? 'today' : `${Math.abs(n)} day${Math.abs(n) === 1 ? '' : 's'}`);
export const tierLabel = (t: string | null | undefined) => (t ? t.charAt(0).toUpperCase() + t.slice(1) : '');
const plural = (n: number, one: string, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

// ---- ConMon cycles ------------------------------------------------------------------------------

export interface CycleItemModel {
  key: string;
  label: string;
  required: boolean;
  done: { eventId: string; at: string; by: string } | null;
  action: { label: string; href: string };
}
export interface CycleModel {
  id: string;
  periodId: string;
  kind: 'conmon' | 'snapshot';
  /** "October ConMon" (named by its due month) or "Snapshot". */
  label: string;
  cadence: 'Monthly' | 'Quarterly' | 'Annual' | string;
  due: string;
  /** Negative when late. */
  daysLeft: number;
  late: boolean;
  done: number;
  total: number;
  /** Required items still open. */
  left: number;
  items: CycleItemModel[];
}

export function cycleLabel(c: Pick<GovrampCycle, 'kind' | 'due_at'>, todayDay: string): string {
  if (c.kind === 'snapshot') return 'Snapshot';
  const [y, m] = c.due_at.slice(0, 7).split('-').map(Number) as [number, number];
  return `${MONTHS[m - 1]}${String(y) === todayDay.slice(0, 4) ? '' : ` ${y}`} ConMon`;
}

export function cycleModel(c: GovrampCycle, todayDay: string): CycleModel {
  const done = new Map(c.items_done.map((d) => [d.item, d]));
  const items: CycleItemModel[] = c.checklist.map((i) => {
    const d = done.get(i.item);
    return { key: i.item, label: i.label, required: i.required, done: d ? { eventId: d.event_id, at: d.recorded_at, by: d.recorded_by } : null, action: i.action };
  });
  const required = items.filter((i) => i.required);
  const doneCount = required.filter((i) => i.done).length;
  const daysLeft = daysBetween(todayDay, c.due_at);
  return {
    id: c.cycle_id, periodId: c.period_id, kind: c.kind, label: cycleLabel(c, todayDay),
    cadence: c.cadence_months === 1 ? 'Monthly' : c.cadence_months === 3 ? 'Quarterly' : c.cadence_months === 12 ? 'Annual' : `Every ${c.cadence_months} months`,
    due: c.due_at, daysLeft, late: c.effective_status === 'late' || daysLeft < 0,
    done: doneCount, total: required.length, left: required.length - doneCount, items,
  };
}

// ---- readiness ----------------------------------------------------------------------------------

export type GovrampControl = NonNullable<GovrampOverview['readiness']>['requirements'][number];

export interface ReadinessModel {
  met: number;
  notMet: GovrampControl[];
  noEvidence: GovrampControl[];
  /** V109-1-19 (B4): controls GAC Pro couldn't check — their own count and list, never "no evidence". */
  couldnt: GovrampControl[];
  total: number;
}
export function readinessModel(o: Pick<GovrampOverview, 'readiness'>): ReadinessModel | null {
  if (!o.readiness) return null;
  const rs = o.readiness.requirements;
  const cc = (r: GovrampControl) => r.status !== 'met' && !!r.couldnt_check;
  return {
    met: o.readiness.counts.met,
    notMet: rs.filter((r) => r.status === 'not_met' && !cc(r)), noEvidence: rs.filter((r) => r.status === 'no_evidence' && !cc(r)), couldnt: rs.filter(cc),
    // V109-5 built-UI: Not applicable controls are outside the count (met of the applicable ones).
    total: o.readiness.counts.total - (o.readiness.counts.not_applicable ?? 0),
  };
}

// ---- the status (program page + Today) ---------------------------------------------------------

export type PrimaryTarget = 'conmon' | 'movers' | 'snapshot' | 'generate' | 'tier';
export interface GovrampStatus {
  world: 'snapshot' | 'tier';
  tone: Tone;
  /** The status line (program page and Today). */
  headline: string;
  /** Today's shorter form where the design gives one. */
  todayHeadline: string;
  consequence: string | null;
  /** The cycle the status is about (the late one, else the next open one). */
  cycle: CycleModel | null;
  date: { label: string; date: string; note: string; late: boolean } | null;
  primary: { label: string; target: PrimaryTarget } | null;
  /** The tier GovRAMP granted (recorded), and the one pursued when different. */
  held: string | null;
  pursuing: string | null;
  lapsed: boolean;
  late: boolean;
  /** A tier change waits while ConMon is late or a lapse notice stands. */
  tierChangeBlocked: 'conmon_late' | 'lapsed' | null;
}

export interface SnapshotMover { requirementId: string; ref: string; title: string; status: 'not_met' | 'no_evidence'; gainPct: number; control: GovrampControl | null }

/** Snapshot controls not yet met, biggest score gain first. */
export function snapshotMovers(o: GovrampOverview): SnapshotMover[] {
  if (!o.snapshot) return [];
  const byId = new Map((o.readiness?.requirements ?? []).map((r) => [r.requirement_id, r]));
  return o.snapshot.score.controls.filter((c) => !c.met)
    .map((c) => {
      const r = byId.get(c.requirement_id) ?? null;
      return { requirementId: c.requirement_id, ref: r?.ref ?? c.requirement_id.split(':').pop()!.toUpperCase(), title: r?.title ?? '', status: (r?.status === 'not_met' ? 'not_met' : 'no_evidence') as SnapshotMover['status'], gainPct: Math.round(c.weight * 10_000) / 100, control: r };
    })
    .sort((a, b) => b.gainPct - a.gainPct || a.ref.localeCompare(b.ref));
}

export const pct = (n: number) => `${n.toFixed(2)}%`;
const sumPct = (ms: SnapshotMover[]) => Math.round(ms.reduce((s, m) => s + m.gainPct, 0) * 100) / 100;

/** The flat-score levers: fix the not-met movers, collect the no-evidence ones (top three of each). */
export function snapshotLevers(o: GovrampOverview): { fix: SnapshotMover[]; collect: SnapshotMover[]; fixGain: number; collectGain: number } {
  const m = snapshotMovers(o);
  const fix = m.filter((x) => x.status === 'not_met').slice(0, 3);
  const collect = m.filter((x) => x.status === 'no_evidence').slice(0, 3);
  return { fix, collect, fixGain: sumPct(fix), collectGain: sumPct(collect) };
}

export function govrampStatus(o: GovrampOverview, todayDay: string): GovrampStatus {
  const cycles = o.cycles.map((c) => cycleModel(c, todayDay));
  const lateCycle = cycles.find((c) => c.late) ?? null;
  const next = lateCycle ?? cycles[0] ?? null;
  const held = o.held?.tier ?? null;
  const pursuing = o.tier && o.tier !== held ? o.tier : null;
  const lapsed = !!o.lapse;
  const blocked: GovrampStatus['tierChangeBlocked'] = lapsed ? 'lapsed' : lateCycle ? 'conmon_late' : null;
  const finish = (c: CycleModel) => (c.left > 0 ? `Finish ${c.label} · ${c.left} left` : `Submit ${c.label}`);
  const base = { held, pursuing, lapsed, late: !!lateCycle, tierChangeBlocked: blocked, cycle: next };

  if (o.tier === 'snapshot') {
    const imp = o.snapshot?.improvement ?? null;
    const flat = !!imp?.warning;
    const snap = cycles.find((c) => c.kind === 'snapshot') ?? next;
    const levers = snapshotLevers(o);
    const primary = flat
      ? (levers.fix.length ? { label: `Fix ${levers.fix.length} · +${pct(levers.fixGain)}`, target: 'movers' as const }
        : levers.collect.length ? { label: `Collect ${levers.collect.length} · +${pct(levers.collectGain)}`, target: 'movers' as const } : null)
      : snap ? { label: 'Prepare Snapshot', target: 'snapshot' as const } : null;
    return {
      ...base, world: 'snapshot', cycle: snap,
      tone: flat || (snap?.late ?? false) ? (snap?.late ? 'bad' : 'warn') : 'ok',
      headline: snap?.late ? 'Snapshot late' : flat ? 'This Snapshot won’t improve your score' : imp?.last_submitted_percent == null ? 'First Snapshot' : 'Snapshot improving',
      todayHeadline: snap?.late ? 'Snapshot late' : flat ? 'Snapshot won’t improve' : imp?.last_submitted_percent == null ? 'First Snapshot' : 'Snapshot improving',
      consequence: null,
      date: snap ? { label: snap.late ? 'Snapshot was due' : 'Next Snapshot due', date: snap.due, note: snap.late ? `${daysText(snap.daysLeft)} late` : daysText(snap.daysLeft), late: snap.late } : null,
      primary,
    };
  }

  const name = tierLabel(held ?? o.tier);
  if (lapsed) {
    return {
      ...base, world: 'tier', tone: 'bad',
      headline: `${name} lapsed`, todayHeadline: `${name} lapsed`,
      consequence: `GovRAMP notified lapse on ${fmtShort(o.lapse!.notice_date, todayDay)}`,
      date: lateCycle ? { label: `${lateCycle.label} was due`, date: lateCycle.due, note: `${daysText(lateCycle.daysLeft)} late`, late: true } : null,
      primary: lateCycle ? { label: `Submit ${lateCycle.label} · then contact GovRAMP`, target: 'conmon' } : null,
    };
  }
  if (lateCycle) {
    return {
      ...base, world: 'tier', tone: 'bad',
      headline: `${name} at risk`, todayHeadline: `${name} at risk`,
      consequence: `GovRAMP escalation if ${lateCycle.label} stays late`,
      date: { label: `${lateCycle.label} was due`, date: lateCycle.due, note: `${daysText(lateCycle.daysLeft)} late`, late: true },
      primary: { label: finish(lateCycle), target: 'conmon' },
    };
  }
  const headline = held ? `${tierLabel(held)} on track` : `Pursuing ${tierLabel(o.tier)}`;
  return {
    ...base, world: 'tier', tone: 'ok', headline, todayHeadline: headline, consequence: null,
    date: next ? { label: `${next.label} due`, date: next.due, note: daysText(next.daysLeft), late: false } : null,
    primary: next ? { label: finish(next), target: 'conmon' } : o.period ? { label: 'Set up ConMon cycles', target: 'generate' } : null,
  };
}

/** The non-interactive tier ladder: Snapshot → Core → Ready → Authorized with held / target / done / lapsed. */
export type RungState = 'done' | 'held' | 'target' | 'lapsed' | 'todo';
export function tierLadder(o: Pick<GovrampOverview, 'tier' | 'held' | 'lapse' | 'last_submitted'>, todayDay: string): Array<{ tier: string; label: string; state: RungState; note: string | null }> {
  const order = ['snapshot', 'core', 'ready', 'authorized'];
  const rank = (t: string | null | undefined) => (t ? order.indexOf(t === 'provisional' ? 'authorized' : t) : -1);
  const snapshotWorld = o.tier === 'snapshot';
  const held = snapshotWorld ? 0 : rank(o.held?.tier);
  const target = snapshotWorld ? 1 : rank(o.tier) > held ? rank(o.tier) : -1;
  const snapSubmitted = o.last_submitted?.kind === 'snapshot' ? o.last_submitted.submitted_at : null;
  return order.map((t, i): { tier: string; label: string; state: RungState; note: string | null } => {
    const label = tierLabel(t);
    if (i < held) return { tier: t, label, state: 'done', note: null };
    if (i === held) {
      if (snapshotWorld) return { tier: t, label, state: 'held', note: snapSubmitted ? `submitted ${fmtShort(snapSubmitted, todayDay)}` : null };
      return o.lapse ? { tier: t, label, state: 'lapsed', note: 'lapsed' } : { tier: t, label, state: 'held', note: 'held' };
    }
    if (i === target) return { tier: t, label, state: 'target', note: 'target' };
    return { tier: t, label, state: 'todo', note: null };
  });
}

/** "Moderate · Core held since Jul 14, 2026 · Lead Dana Ruiz" (the header meta line). */
export function headerMeta(o: GovrampOverview, s: GovrampStatus, lead: string | null, todayDay: string): string {
  const parts: string[] = [tierLabel(o.impact_level) || 'Moderate'];
  if (o.tier === 'snapshot') parts.push('Pursuing Core');
  else if (s.lapsed) parts.push(`${tierLabel(s.held ?? o.tier)} lapsed`);
  else if (s.held && s.pursuing) parts.push(`${tierLabel(s.held)} held · pursuing ${tierLabel(s.pursuing)}`);
  else if (s.held) parts.push(`${tierLabel(s.held)} held since ${fmtShort(o.held!.granted_on, todayDay)}`);
  else parts.push(`Pursuing ${tierLabel(o.tier)}`);
  if (lead) parts.push(`Lead ${lead}`);
  return parts.join(' · ');
}

/** The tier after this one (Core → Ready → Authorized); null at the top. */
export function nextTier(t: string | null | undefined): string | null {
  return t === 'snapshot' ? 'core' : t === 'core' ? 'ready' : t === 'ready' ? 'authorized' : null;
}

// ---- FedRAMP Class A ----------------------------------------------------------------------------

export type RuleStatus = 'done' | 'not' | 'noev' | 'ns' | 'act';
export interface ClassARule {
  ref: string;
  title: string;
  family: string | null;
  status: RuleStatus;
  /** Why it is in this state (plain words, no rule ids). */
  meta: string;
  /** Waiting on a booking (Ready assessment, Class B) rather than on work: counted apart in the hero (acceptance #1). */
  waitingOnBooking: boolean;
  /** What a not-started item waits on, so its action points there (the prerequisite, the Class B booking, the listing date). */
  waits: 'materials' | 'class_b' | 'listing' | null;
  /** The KSI rows carry their KSI id; other rule ids stay inside the "Why" disclosure. */
  isKsi: boolean;
}

export interface ClassAWorld {
  /** The GovRAMP state the Class A page sits in (one world per entry point). */
  kind: 'materials-current' | 'materials-expiring' | 'materials-expired' | 'materials-missing' | 'requires-ready' | 'requires-ready-late' | 'pursuing-ready' | 'govramp-lapsed';
  tone: Tone | 'gate';
  alarm: string | null;
  consequence: string | null;
  /** The Ready booking (one record, read from GovRAMP). */
  booking: { firm: string; date: string } | null;
}

const READY_PLUS = new Set(['ready', 'authorized', 'provisional']);

/** Which world the Class A page is in: materials first (they decide Class A), else the GovRAMP state. */
export function classAWorld(materials: ClassAMaterialsItem | null, gov: GovrampOverview | null, listingDate: string | null, todayDay: string): ClassAWorld {
  const booking = gov?.ready_booking ? { firm: gov.ready_booking.firm, date: gov.ready_booking.assessment_date } : null;
  const s = gov ? govrampStatus(gov, todayDay) : null;
  const heldReady = !!gov?.held && READY_PLUS.has(gov.held.tier);
  if (materials?.state === 'met') {
    if (listingDate && materials.expires_at && materials.expires_at < listingDate) {
      return { kind: 'materials-expiring', tone: 'warn', alarm: `Ready materials expire ${fmtShort(materials.expires_at, todayDay)} · before your ${fmtShort(listingDate, todayDay)} listing`, consequence: null, booking };
    }
    return { kind: 'materials-current', tone: 'ok', alarm: null, consequence: null, booking };
  }
  if (materials?.state === 'expired') {
    return { kind: 'materials-expired', tone: 'bad', alarm: `Ready materials expired${materials.expires_at ? ` ${fmtShort(materials.expires_at, todayDay)}` : ''}`, consequence: null, booking };
  }
  if (gov && s) {
    if (s.lapsed) return { kind: 'govramp-lapsed', tone: 'bad', alarm: `GovRAMP ${tierLabel(s.held ?? gov.tier)} lapsed · notified ${fmtShort(gov.lapse!.notice_date, todayDay)}`, consequence: null, booking };
    if (heldReady) return { kind: 'materials-missing', tone: 'warn', alarm: 'Ready materials not uploaded', consequence: null, booking };
    if (s.late) {
      return { kind: 'requires-ready-late', tone: 'warn', alarm: 'Blocked · needs GovRAMP Ready',
        consequence: `GovRAMP ${tierLabel(s.held ?? gov.tier)} at risk · ${s.cycle!.label} ${daysText(s.cycle!.daysLeft)} late`, booking };
    }
    if (gov.tier && READY_PLUS.has(gov.tier)) {
      return { kind: 'pursuing-ready', tone: 'warn', alarm: booking ? `Ready assessment booked · ${booking.firm} · ${fmtShort(booking.date, todayDay)}` : 'Blocked · Ready assessment not booked', consequence: null, booking };
    }
    return { kind: 'requires-ready', tone: 'warn', alarm: 'Blocked · needs GovRAMP Ready', consequence: null, booking };
  }
  return { kind: 'materials-missing', tone: 'warn', alarm: 'Ready materials not uploaded', consequence: null, booking };
}

type Req = FrameworkReadiness['requirements'][number];

/**
 * One rule's status from readiness: met → done; partial → needs action; a gap
 * with a failing control → not met; a gap with no evidence yet → no evidence;
 * nothing mapped → not started. The rules the Class A items speak for (their
 * own rule ids, from the server) take the item's state instead.
 */
export function classARules(reqs: readonly Req[], materials: ClassAMaterialsItem | null, clocks: readonly ClassAClock[], world: ClassAWorld | null): ClassARule[] {
  const matIds = new Set(materials?.rule_ids ?? []);
  const clockOf = new Map<string, ClassAClock>(clocks.map((c) => [c.rule_id, c]));
  return reqs.map((r): ClassARule => {
    const isKsi = /^KSI-/.test(r.ref);
    const base = { ref: r.ref, title: r.title, family: r.group_name ?? r.group_ref, isKsi, waitingOnBooking: false, waits: null as ClassARule['waits'] };
    if (materials && matIds.has(r.ref)) {
      if (materials.state === 'met') return { ...base, status: 'done', meta: 'Includes your alternative-framework materials' };
      if (materials.state === 'expired') return { ...base, status: 'act', meta: 'Materials expired' };
      const waiting = !!world && ['requires-ready', 'requires-ready-late', 'pursuing-ready', 'govramp-lapsed'].includes(world.kind);
      return { ...base, status: 'ns', meta: waiting ? 'Waiting on GovRAMP Ready or a SOC 2 Type II report' : 'Waiting on assessment materials', waits: 'materials', waitingOnBooking: waiting && world!.kind === 'pursuing-ready' && !world!.booking };
    }
    const clock = clockOf.get(r.ref);
    if (clock) {
      if (clock.state === 'satisfied' || clock.state === 'satisfied_late') return { ...base, status: 'done', meta: 'Class B assessment scheduled' };
      return clock.state === 'not_listed'
        ? { ...base, status: 'ns', meta: 'Listing date not set', waits: 'listing' }
        : { ...base, status: 'ns', meta: 'Class B assessment not booked', waits: 'class_b', waitingOnBooking: true };
    }
    if (r.status === 'met') return { ...base, status: 'done', meta: 'Met' };
    if (r.status === 'partial') return { ...base, status: 'act', meta: r.reason || 'Partly met' };
    if (r.status === 'unmapped') return { ...base, status: 'ns', meta: 'Not started' };
    const failing = r.crediting_controls.some((c) => c.state === 'fail' || c.state === 'stale');
    return failing ? { ...base, status: 'not', meta: 'Not met' } : { ...base, status: 'noev', meta: 'No evidence yet' };
  });
}

/**
 * Acceptance #1: the hero's Fix / Collect / Start labels come from the SAME
 * totals as the legend. "Start N" equals "N not started"; when some of those
 * wait on a booking the label splits: "N to start · M waiting on a booking".
 */
export interface ClassAHeroCounts {
  total: number;
  done: number;
  act: number;
  not: number;
  noev: number;
  ns: number;
  waiting: number;
  fixLabel: string | null;
  collectLabel: string | null;
  startLabel: string | null;
}
export function classAHeroCounts(rules: readonly ClassARule[]): ClassAHeroCounts {
  const n = (s: RuleStatus) => rules.filter((r) => r.status === s).length;
  const done = n('done'), act = n('act'), not = n('not'), noev = n('noev'), ns = n('ns');
  const waiting = rules.filter((r) => r.status === 'ns' && r.waitingOnBooking).length;
  const toStart = ns - waiting;
  return {
    total: rules.length, done, act, not, noev, ns, waiting,
    fixLabel: not ? `Fix ${not}` : null,
    collectLabel: noev ? `Collect ${noev}` : null,
    startLabel: !ns ? null : waiting === 0 ? `Start ${ns}` : toStart > 0 ? `${toStart} to start · ${waiting} waiting on a booking` : `${plural(waiting, 'waits', 'wait')} on a booking`,
  };
}

/** The legend under the Class A meter (same totals as the hero labels). */
export function classALegend(c: ClassAHeroCounts): Array<{ key: RuleStatus; text: string }> {
  const out: Array<{ key: RuleStatus; text: string }> = [{ key: 'done', text: `${c.done} done` }];
  if (c.act) out.push({ key: 'act', text: `${c.act} needs action` });
  out.push({ key: 'not', text: `${c.not} not met` }, { key: 'noev', text: `${c.noev} no evidence` }, { key: 'ns', text: `${c.ns} not started` });
  return out;
}

